import { ref } from 'vue'
import { useStoreReviews } from '@/stores/storeReviews'

/**
 * Composable for shared share actions (image downloads, link copying, WhatsApp sharing)
 */
export function useShareActions() {
  const storeReviews = useStoreReviews()
  const downloadingVariant = ref(null)
  const linkCopied = ref(false)

  /**
   * Download a review image variant
   * @param {Object} options
   * @param {Object} options.review - Review object
   * @param {Object} options.title - Title object (for filename)
   * @param {string} options.variant - Image variant ('plain', 'with_text', 'social_share')
   * @param {Object} options.imageBlobs - Optional pre-generated image blobs
   */
  const downloadImage = async ({
    review,
    title,
    variant,
    imageBlobs = null
  }) => {
    if (!review?.id) return

    downloadingVariant.value = variant

    try {
      let blob = null

      // Try to use pre-generated blobs first
      if (imageBlobs?.[variant]) {
        blob = imageBlobs[variant]
      } else if (review?.id) {
        // Fetch from storage
        blob = await storeReviews.fetchReviewImageBlob(review.id, variant)
      }

      if (blob) {
        // Create download link
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const titleSlug = (
          title?.title ||
          title?.name ||
          review?.title_title ||
          'review'
        )
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
        a.download = `${titleSlug}-review-${variant}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } else {
        console.error('No image available for variant:', variant)
      }
    } catch (error) {
      console.error('Failed to download image:', error)
    } finally {
      downloadingVariant.value = null
    }
  }

  /**
   * Copy a link to clipboard
   * @param {string} link - The link to copy (defaults to current page URL)
   */
  const copyLink = async (link = null) => {
    try {
      const linkToCopy = link || window.location.href
      await navigator.clipboard.writeText(linkToCopy)
      linkCopied.value = true
      setTimeout(() => {
        linkCopied.value = false
      }, 2000)
      
      // Track link copy in PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { track } = usePostHog()
          track('Link Shared', {
            platform: 'copy',
            url: linkToCopy,
            is_current_page: !link
          })
        })
      }
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }

  /**
   * Share via WhatsApp
   * @param {Object} options
   * @param {string} options.link - The link to share
   * @param {string} options.message - Optional custom message
   */
  const shareToWhatsApp = ({ link = null, message = null }) => {
    const linkToShare = link || window.location.href
    let shareMessage = message

    if (!shareMessage) {
      shareMessage = `Check this out: ${linkToShare}`
    }

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`
    window.open(whatsappUrl, '_blank')
    
    // Track WhatsApp share in PostHog
    if (import.meta.env.VITE_APP_MODE === 'production') {
      import('@/composables/usePostHog').then(({ usePostHog }) => {
        const { track } = usePostHog()
        track('Link Shared', {
          platform: 'whatsapp',
          url: linkToShare,
          is_current_page: !link,
          has_custom_message: Boolean(message)
        })
      })
    }
  }

  return {
    downloadingVariant,
    linkCopied,
    downloadImage,
    copyLink,
    shareToWhatsApp
  }
}
